import { CheckIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { ContextMenuItemType } from 'data/types';
import useClampedMenuPosition from 'hooks/use-clamped-menu-position';
import { useCallback, useRef, useState } from 'react';
import { AnchorRect } from 'utils/context-menu-position';

type ContextMenuProps = {
  visible: boolean;
  x: number;
  y: number;
  items: ContextMenuItemType[];
  onClose: () => void;
  className?: string;
  isSubmenu?: boolean;
  anchor?: AnchorRect;
};

type RenderedSubmenuPanelProps = Readonly<{
  anchor: AnchorRect;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClose: () => void;
  children: React.ReactNode;
}>;

function RenderedSubmenuPanel({
  anchor,
  onMouseEnter,
  onMouseLeave,
  onClose,
  children
}: RenderedSubmenuPanelProps) {
  const { ref, position } = useClampedMenuPosition({
    x: anchor.right,
    y: anchor.top,
    anchor
  });

  return (
    <div
      ref={ref}
      role='menu'
      tabIndex={-1}
      data-app-context-menu=''
      className='fixed z-[2001] rounded border border-gray-300 bg-white shadow-lg'
      style={{ left: position.x, top: position.y }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
    >
      {children}
    </div>
  );
}

export default function ContextMenu({
  visible,
  x,
  y,
  items,
  onClose,
  className,
  isSubmenu,
  anchor
}: ContextMenuProps) {
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const [submenuAnchor, setSubmenuAnchor] = useState<AnchorRect | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { ref: menuRef, position } = useClampedMenuPosition({ x, y, anchor });

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (index: number, event: React.MouseEvent<HTMLButtonElement>) => {
      clearCloseTimeout();

      const item = items[index];

      if (
        !item.separator &&
        (item.submenu || item.renderSubmenu) &&
        !item.disabled
      ) {
        const buttonRect = event.currentTarget.getBoundingClientRect();

        setSubmenuAnchor({
          left: buttonRect.left,
          right: buttonRect.right,
          top: buttonRect.top,
          bottom: buttonRect.bottom
        });
        setActiveSubmenu(index);
      } else if (!item.separator) {
        setActiveSubmenu(null);
      }
    },
    [items, clearCloseTimeout]
  );

  const handleMouseLeave = useCallback(() => {
    clearCloseTimeout();

    closeTimeoutRef.current = setTimeout(() => {
      setActiveSubmenu(null);
      closeTimeoutRef.current = null;
    }, 150);
  }, [clearCloseTimeout]);

  const handleSubmenuMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleSubmenuMouseLeave = useCallback(() => {
    setActiveSubmenu(null);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <>
      <div
        ref={menuRef}
        role='menu'
        tabIndex={-1}
        data-app-context-menu=''
        className={clsx(
          'fixed max-h-[364px] min-w-[160px] overflow-y-auto rounded border border-gray-300 bg-white py-1 shadow-lg',
          isSubmenu ? 'z-[2001]' : 'z-[2000]',
          className
        )}
        style={{ left: position.x, top: position.y }}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`menu-item-${index}`}>
            {item.separator && (
              <div className='mx-1 my-1 border-t border-gray-200' />
            )}
            {!item.separator && item.loading && (
              <div className='flex items-center justify-center px-3 py-2'>
                <svg
                  className='h-4 w-4 animate-spin text-gray-400'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
              </div>
            )}
            {!item.separator && !item.loading && (
              <button
                type='button'
                disabled={item.disabled}
                className={clsx(
                  'relative w-full py-2 text-left text-sm transition-colors',
                  item.checked != null ? 'pl-7 pr-3' : 'px-3',
                  item.disabled
                    ? 'cursor-not-allowed text-gray-400'
                    : 'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
                  (item.submenu || item.renderSubmenu) &&
                    !item.disabled &&
                    'pr-8'
                )}
                onMouseEnter={(e) => handleMouseEnter(index, e)}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    !item.disabled &&
                    item.onClick &&
                    !item.submenu &&
                    !item.renderSubmenu
                  ) {
                    item.onClick();
                    if (!item.keepOpen) {
                      onClose();
                    }
                  }
                }}
              >
                {item.checked && (
                  <CheckIcon className='absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2' />
                )}
                {item.label}
                {(item.submenu || item.renderSubmenu) && !item.disabled && (
                  <span className='absolute right-2 top-1/2 -translate-y-1/2 text-xs'>
                    <ChevronRightIcon className='h-4 w-4' />
                  </span>
                )}
              </button>
            )}
          </div>
        ))}
      </div>
      {activeSubmenu !== null &&
        submenuAnchor !== null &&
        !items[activeSubmenu]?.separator &&
        items[activeSubmenu]?.renderSubmenu && (
          <RenderedSubmenuPanel
            anchor={submenuAnchor}
            onMouseEnter={handleSubmenuMouseEnter}
            onMouseLeave={handleSubmenuMouseLeave}
            onClose={onClose}
          >
            {items[activeSubmenu].renderSubmenu!({ onClose })}
          </RenderedSubmenuPanel>
        )}
      {activeSubmenu !== null &&
        submenuAnchor !== null &&
        !items[activeSubmenu]?.separator &&
        items[activeSubmenu]?.submenu &&
        !items[activeSubmenu]?.renderSubmenu && (
          <div
            onMouseEnter={handleSubmenuMouseEnter}
            onMouseLeave={handleSubmenuMouseLeave}
          >
            <ContextMenu
              x={submenuAnchor.right}
              y={submenuAnchor.top}
              items={items[activeSubmenu].submenu}
              onClose={onClose}
              visible
              isSubmenu
              anchor={submenuAnchor}
            />
          </div>
        )}
    </>
  );
}
